import {
  RecordContextProvider,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { InfiniteList } from "@/components/admin/infinite-list";
import { SortButton } from "@/components/admin/sort-button";

import { TopToolbar } from "../layout/TopToolbar";
import { FullHeightListLayout } from "../misc/FullHeightListLayout";
import { VirtualizedGridContent } from "../misc/VirtualizedGridContent";
import type { Company } from "../types";
import { CompanyCard } from "./CompanyCard";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyListFilter } from "./CompanyListFilter";
import { useCompanyFilterBar } from "./companyFilterBar";
import { ActiveFilterBar } from "../misc/ActiveFilterBar";

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <InfiniteList
      title={false}
      perPage={50}
      sort={{ field: "name", order: "ASC" }}
      actions={<CompanyListActions />}
    >
      <CompanyListLayout />
    </InfiniteList>
  );
};

const CompanyListLayout = () => {
  const translate = useTranslate();
  const { data, isPending, isFetching, filterValues } =
    useListContext<Company>();
  const filterBar = useCompanyFilterBar();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  // Wait for a refetch before showing the onboarding screen: removing the last
  // filter from an empty result would otherwise flash it before the rows land.
  if (!data?.length && !hasFilters && !isFetching) return <CompanyEmpty />;

  return (
    <FullHeightListLayout
      sidebar={<CompanyListFilter />}
      header={<ActiveFilterBar {...filterBar} className="border-b px-4 py-2" />}
    >
      {data?.length ? (
        <VirtualizedGridContent<Company>
          itemContent={(_, company) => (
            <RecordContextProvider value={company}>
              <CompanyCard />
            </RecordContextProvider>
          )}
        />
      ) : (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.companies.empty.title", {
              _: "No companies found",
            })}
          </div>
        </div>
      )}
    </FullHeightListLayout>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <SortButton fields={["name", "created_at", "nb_contacts"]} />
      <ExportButton />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};
